import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container, Typography, Box, Card, CardContent, CardActions,
  Avatar, Button, Select, MenuItem, FormControl,
  InputLabel, OutlinedInput, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Chip, Fab,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import BusinessIcon from "@mui/icons-material/Business";
import AddIcon from "@mui/icons-material/Add";
import MessageIcon from "@mui/icons-material/Message";
import { companies as companiesApi, locations as locationsApi, inquiries, COMPANY_TYPE_IDS } from "../api/endpoints";
import type { Company, CompanyType } from "../types";
import PageHeader from "../components/PageHeader";
import { alpha } from "@mui/material/styles";

// Maps each CompanyType string to its translation key
const COMPANY_TYPE_KEYS: Record<CompanyType, string> = {
  "Grocery and Convenience Store": "companyTypes.groceryAndConvenienceStore",
  "Information Technology":        "companyTypes.informationTechnology",
  "Food and Restaurant":           "companyTypes.foodAndRestaurant",
  "Personal Services":             "companyTypes.personalServices",
  Business:                        "companyTypes.business",
  Engineering:                     "companyTypes.engineering",
  Education:                       "companyTypes.education",
  "Finance and Insurance":         "companyTypes.financeAndInsurance",
  "Health Services":               "companyTypes.healthServices",
  "Hotels and Motels":             "companyTypes.hotelsAndMotels",
  "Motor Vehicle":                 "companyTypes.motorVehicle",
  "Agriculture and Wildlife":      "companyTypes.agricultureAndWildlife",
  Entertainment:                   "companyTypes.entertainment",
  "Real Estate and Housing":       "companyTypes.realEstateAndHousing",
  "Safety, Security and Legal":    "companyTypes.safetySecurityAndLegal",
  Transportation:                  "companyTypes.transportation",
  Other:                           "companyTypes.other",
};
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useUserSettings } from "../context/UserSettingsContext";
import LocationPicker, { EMPTY_LOCATION, LocationFormState } from "../components/LocationPicker";
import { countryNameFromCode } from "../utils/locationUtils";

const ALL_TYPES = Object.keys(COMPANY_TYPE_IDS) as CompanyType[];

// Deterministic accent color per company type — gives the list the same
// colorful, varied feel as post-type chips in the feed.
const TYPE_ACCENTS = ["#0F2156", "#B91C2C", "#D97706", "#16A34A", "#7C3AED", "#0891B2"];
function typeAccent(type: string): string {
  let hash = 0;
  for (let i = 0; i < type.length; i++) hash = (hash * 31 + type.charCodeAt(i)) >>> 0;
  return TYPE_ACCENTS[hash % TYPE_ACCENTS.length];
}

export default function CompanyPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { countryCode } = useUserSettings();
  const [list, setList] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<CompanyType | "">("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: "",
    description: "",
    phone_number: "",
    email: "",
    company_type: "" as CompanyType | "",
  });
  const [locationForm, setLocationForm] = useState<LocationFormState>(EMPTY_LOCATION);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createFieldErrors, setCreateFieldErrors] = useState<Record<string, string>>({});

  const [inquireOpen, setInquireOpen] = useState(false);
  const [inquireCompany, setInquireCompany] = useState<Company | null>(null);
  const [inquireMsg, setInquireMsg] = useState("");
  const [inquireLoading, setInquireLoading] = useState(false);

  const [likedSet, setLikedSet] = useState<Set<number>>(new Set());

  const load = useCallback(async (p: number, type: CompanyType | "") => {
    setLoading(true);
    setError(null);
    try {
      const res = await companiesApi.list({ company_type: type || undefined, page: p, limit: 20 });
      if (p === 1) setList(res.data ?? []);
      else setList((prev) => [...prev, ...(res.data ?? [])]);
      if (res.meta) setTotalPages(res.meta.total_pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.noResults"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    setPage(1);
    load(1, filter);
  }, [filter, load]);

  const handleLike = async (co: Company) => {
    try {
      if (likedSet.has(co.id)) {
        await companiesApi.unlike(co.id);
        setLikedSet((s) => { const n = new Set(s); n.delete(co.id); return n; });
        setList((prev) => prev.map((c) => c.id === co.id ? { ...c, likes_count: Math.max((c.likes_count ?? 1) - 1, 0) } : c));
      } else {
        await companiesApi.like(co.id);
        setLikedSet((s) => new Set(s).add(co.id));
        setList((prev) => prev.map((c) => c.id === co.id ? { ...c, likes_count: (c.likes_count ?? 0) + 1 } : c));
      }
    } catch { /* ignore */ }
  };

  const handleOpenCreate = () => {
    setCreateForm({ name: "", description: "", phone_number: "", email: "", company_type: "" });
    setLocationForm({ ...EMPTY_LOCATION, country_code: countryCode || "" });
    setCreateError(null);
    setCreateFieldErrors({});
    setCreateOpen(true);
  };

  const validateCreate = (): boolean => {
    const errs: Record<string, string> = {};
    const name = createForm.name.trim();
    const desc = createForm.description.trim();
    if (!name) errs.name = t("companyValidation.nameRequired");
    else if (name.length < 2) errs.name = t("companyValidation.nameTooShort");
    else if (name.length > 100) errs.name = t("companyValidation.nameTooLong");
    if (!desc) errs.description = t("companyValidation.descriptionRequired");
    else if (desc.length < 10) errs.description = t("companyValidation.descriptionTooShort");
    if (!createForm.company_type) errs.company_type = t("companyValidation.typeRequired");
    if (!locationForm.country_code) errs.country = t("companyValidation.countryRequired");
    if (createForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createForm.email)) {
      errs.email = t("companyValidation.emailInvalid");
    }
    setCreateFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCreate = async () => {
    if (!validateCreate()) return;
    setCreateLoading(true);
    setCreateError(null);
    try {
      let locationId: number | null = null;
      if (locationForm.country_code) {
        const loc = await locationsApi.create({
          address_line1: locationForm.address_line1 || locationForm.city || countryNameFromCode(locationForm.country_code),
          city: locationForm.city || null,
          state_province: locationForm.state_province || null,
          country_code: locationForm.country_code,
          latitude: locationForm.latitude ?? null,
          longitude: locationForm.longitude ?? null,
        });
        locationId = loc?.id ?? null;
      }
      await companiesApi.create({
        name: createForm.name.trim(),
        description: createForm.description.trim(),
        phone_number: createForm.phone_number || null,
        email: createForm.email || null,
        company_type_id: COMPANY_TYPE_IDS[createForm.company_type as CompanyType],
        location_id: locationId,
      });
      setCreateOpen(false);
      setCreateForm({ name: "", description: "", phone_number: "", email: "", company_type: "" });
      setLocationForm(EMPTY_LOCATION);
      load(1, filter);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : t("common.noResults"));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleInquire = async () => {
    if (!inquireCompany || !user) return;
    setInquireLoading(true);
    try {
      await inquiries.create({
        receiver_id: inquireCompany.owner_id,
        entity_type: "company",
        entity_id: inquireCompany.id,
        message: inquireMsg,
      });
      setInquireOpen(false);
      setInquireMsg("");
    } catch { /* ignore */ }
    finally { setInquireLoading(false); }
  };

  return (
    <>
    <PageHeader title={t("business.pageTitle")} icon={<BusinessIcon sx={{ color: "white", fontSize: 22 }} />} />
    <Container maxWidth="sm" sx={{ py: 2 }}>
      <FormControl size="small" fullWidth sx={{ mb: 2 }}>
        <InputLabel>{t("business.filterByType")}</InputLabel>
        <Select
          value={filter}
          label={t("business.filterByType")}
          onChange={(e) => setFilter(e.target.value as CompanyType | "")}
        >
          <MenuItem value="">{t("business.allTypes")}</MenuItem>
          {ALL_TYPES.map((type) => (
            <MenuItem key={type} value={type}>{t(COMPANY_TYPE_KEYS[type])}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {list.length === 0 && !loading && (
        <Typography color="text.secondary" textAlign="center" py={6}>
          {t("business.noCompanies")}
        </Typography>
      )}

      {list.map((co) => (
        <Card
          key={co.id}
          sx={{ mb: 2, cursor: "pointer", borderLeft: `4px solid ${typeAccent(co.company_type)}` }}
          onClick={() => navigate(`/app/company/${co.id}`)}
        >
          <CardContent>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Avatar sx={{ background: "linear-gradient(135deg, #1a3a7a 0%, #0F2156 100%)", width: 44, height: 44 }}>
                <BusinessIcon />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={600}>{co.name}</Typography>
                <Chip
                  label={t(COMPANY_TYPE_KEYS[co.company_type as CompanyType] ?? "companyTypes.other")}
                  size="small"
                  sx={{
                    fontSize: "0.65rem",
                    mb: 0.5,
                    fontWeight: 700,
                    color: "white",
                    background: "linear-gradient(135deg, #E8A020 0%, #B9781A 100%)",
                  }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    wordBreak: "break-word",
                  }}
                >
                  {co.description}
                </Typography>
                {co.phone_number && (
                  <Typography variant="caption" color="text.secondary" display="block">📞 {co.phone_number}</Typography>
                )}
                {co.email && (
                  <Typography variant="caption" color="text.secondary" display="block">✉️ {co.email}</Typography>
                )}
                {co.owner && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t("business.owner")}: {co.owner.first_name} {co.owner.last_name}
                  </Typography>
                )}
              </Box>
            </Box>
          </CardContent>
          <CardActions sx={{ px: 2, pt: 0, pb: 1.5 }}>
            {(() => {
              const liked = likedSet.has(co.id);
              return (
                <Box
                  onClick={user ? (e) => { e.stopPropagation(); handleLike(co); } : undefined}
                  sx={{
                    display: "flex", alignItems: "center", gap: 0.6,
                    px: 1.4, py: 0.5, borderRadius: 20,
                    cursor: user ? "pointer" : "default",
                    border: `1.5px solid ${liked ? "transparent" : alpha("#B91C2C", 0.3)}`,
                    backgroundImage: liked ? "linear-gradient(135deg, #DC2626 0%, #B91C2C 100%)" : "none",
                    bgcolor: liked ? "transparent" : alpha("#B91C2C", 0.06),
                    color: liked ? "#fff" : "#B91C2C",
                    boxShadow: liked ? `0 3px 10px ${alpha("#B91C2C", 0.35)}` : "none",
                    transition: "all 0.15s",
                    "&:hover": user ? { bgcolor: liked ? undefined : alpha("#B91C2C", 0.12), borderColor: "#B91C2C" } : {},
                  }}
                >
                  {liked ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
                  <Typography variant="caption" fontWeight={700} sx={{ fontSize: "0.75rem" }}>
                    {co.likes_count ?? 0}
                  </Typography>
                </Box>
              );
            })()}
            {user && co.owner_id !== user.id && (
              <Box
                onClick={(e) => { e.stopPropagation(); setInquireCompany(co); setInquireOpen(true); }}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.6,
                  ml: "auto",
                  px: 1.4, py: 0.5, borderRadius: 20,
                  cursor: "pointer",
                  border: `1.5px solid ${alpha("#B9781A", 0.35)}`,
                  bgcolor: alpha("#E8A020", 0.1),
                  color: "#8A5D14",
                  transition: "all 0.15s",
                  "&:hover": {
                    backgroundImage: "linear-gradient(135deg, #E8A020 0%, #B9781A 100%)",
                    bgcolor: "transparent",
                    color: "#fff",
                    borderColor: "transparent",
                    boxShadow: `0 3px 10px ${alpha("#B9781A", 0.35)}`,
                  },
                }}
              >
                <MessageIcon sx={{ fontSize: "0.9rem" }} />
                <Typography variant="caption" fontWeight={700} sx={{ fontSize: "0.75rem" }}>
                  {t("business.inquire")}
                </Typography>
              </Box>
            )}
          </CardActions>
        </Card>
      ))}

      {loading && <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>}

      {!loading && page < totalPages && (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Button variant="outlined" onClick={() => { const n = page + 1; setPage(n); load(n, filter); }}>
            {t("common.loadMore")}
          </Button>
        </Box>
      )}

      {user && (
        <Fab color="primary" aria-label="add company" onClick={handleOpenCreate}
          sx={{ position: "fixed", bottom: 72, right: 16 }}>
          <AddIcon />
        </Fab>
      )}

      {/* Create Company Dialog */}
      <Dialog open={createOpen} onClose={() => !createLoading && setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("business.createCompany")}</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 1, mt: 1 }}>{createError}</Alert>}
          <TextField
            label={t("business.name")} fullWidth margin="normal"
            value={createForm.name}
            onChange={(e) => { setCreateForm((f) => ({ ...f, name: e.target.value })); setCreateFieldErrors((e2) => ({ ...e2, name: "" })); }}
            placeholder={t("business.namePlaceholder")} required
            error={!!createFieldErrors.name}
            helperText={createFieldErrors.name}
          />
          <TextField
            label={t("business.description")} fullWidth margin="normal" multiline rows={3}
            value={createForm.description}
            onChange={(e) => { setCreateForm((f) => ({ ...f, description: e.target.value })); setCreateFieldErrors((e2) => ({ ...e2, description: "" })); }}
            placeholder={t("business.descriptionPlaceholder")} required
            error={!!createFieldErrors.description}
            helperText={createFieldErrors.description}
          />
          <TextField
            label={t("business.phoneOptional")} fullWidth margin="normal"
            value={createForm.phone_number}
            onChange={(e) => setCreateForm((f) => ({ ...f, phone_number: e.target.value }))}
          />
          <TextField
            label={t("business.emailOptional")} fullWidth margin="normal"
            value={createForm.email}
            onChange={(e) => { setCreateForm((f) => ({ ...f, email: e.target.value })); setCreateFieldErrors((e2) => ({ ...e2, email: "" })); }}
            error={!!createFieldErrors.email}
            helperText={createFieldErrors.email}
          />
          <FormControl fullWidth margin="normal" required error={!!createFieldErrors.company_type}>
            <InputLabel shrink id="create-company-type-label">{t("business.companyType")}</InputLabel>
            <Select
              labelId="create-company-type-label"
              value={createForm.company_type}
              onChange={(e) => {
                setCreateForm((f) => ({ ...f, company_type: e.target.value as CompanyType }));
                setCreateFieldErrors((e2) => ({ ...e2, company_type: "" }));
              }}
              displayEmpty
              input={<OutlinedInput notched label={t("business.companyType")} />}
            >
              <MenuItem value="" disabled>
                <em>{t("business.selectCompanyType")}</em>
              </MenuItem>
              {ALL_TYPES.map((type) => (
                <MenuItem key={type} value={type}>{t(COMPANY_TYPE_KEYS[type])}</MenuItem>
              ))}
            </Select>
            {createFieldErrors.company_type && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {createFieldErrors.company_type}
              </Typography>
            )}
          </FormControl>
          <LocationPicker value={locationForm} onChange={(v) => { setLocationForm(v); setCreateFieldErrors((e2) => ({ ...e2, country: "" })); }} required />
          {createFieldErrors.country && (
            <Alert severity="error" sx={{ mt: 1 }}>{createFieldErrors.country}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={createLoading}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={createLoading}
            sx={{ color: "white", "&.Mui-disabled": { color: "rgba(255,255,255,0.7)", bgcolor: "rgba(0,0,0,0.25)" } }}
          >
            {createLoading ? t("common.creating") : t("common.create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inquire Dialog */}
      <Dialog open={inquireOpen} onClose={() => setInquireOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("business.sendInquiry")} — {inquireCompany?.name}</DialogTitle>
        <DialogContent>
          <TextField
            label={t("business.inquiryMessage")} fullWidth margin="normal" multiline rows={4}
            value={inquireMsg}
            onChange={(e) => setInquireMsg(e.target.value)}
            placeholder={t("business.inquiryPlaceholder")}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setInquireOpen(false)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleInquire}
            disabled={inquireLoading || !inquireMsg.trim()}
            sx={{ color: "white", "&.Mui-disabled": { color: "rgba(255,255,255,0.7)", bgcolor: "rgba(0,0,0,0.25)" } }}
          >
            {inquireLoading ? t("common.sending") : t("common.send")}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </>
  );
}
